// [RED TEAM] - Modified for red-teaming AI safety
// Contains temporary test mode UI - remove after testing

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { OrganizationSearch } from '@/components/OrganizationSearch';
import { AccessCodeGate } from '@/components/AccessCodeGate';
import { LanguageToggle } from '@/components/LanguageToggle';
import { checkAccessCodeStatus, type AccessCodeStatus } from '@/lib/access-code-api';
import { Info, LogIn, AlertTriangle, FlaskConical, Loader2 } from 'lucide-react';
import type { EpicOrganization } from '../../../shared/types/epic-organization';

// [RED TEAM] - Test patient type
interface TestPatient {
  id: string;
  displayName: string;
  condition: string;
  label: string;
}

export const Login = () => {
  const { t } = useTranslation();
  const [selectedOrganization, setSelectedOrganization] = useState<EpicOrganization | null>(null);
  // [RED TEAM] - Test mode state
  const [testPatients, setTestPatients] = useState<TestPatient[]>([]);
  const [selectedTestPatient, setSelectedTestPatient] = useState<string>('');
  const [isTestModeLoading, setIsTestModeLoading] = useState(false);
  const [testModeError, setTestModeError] = useState<string | null>(null);
  // Access code gate state
  const [accessCodeStatus, setAccessCodeStatus] = useState<AccessCodeStatus | null>(null);
  const [accessCodeLoading, setAccessCodeLoading] = useState(true);

  // [RED TEAM] - Check if test mode organization is selected
  const isTestMode = selectedOrganization?.id === 'red-team-testing';

  // Check access code requirement on mount
  useEffect(() => {
    const check = async () => {
      try {
        const status = await checkAccessCodeStatus();
        setAccessCodeStatus(status);
      } catch {
        // If check fails, assume not required (fail open for non-study use)
        setAccessCodeStatus({ required: false, verified: false, code: null });
      } finally {
        setAccessCodeLoading(false);
      }
    };
    check();
  }, []);

  // [RED TEAM] - Fetch test patients when component mounts
  useEffect(() => {
    const fetchTestPatients = async () => {
      try {
        const response = await fetch('/api/auth/test-patients');
        const data = await response.json();
        if (data.enabled && data.patients) {
          setTestPatients(data.patients);
        }
      } catch (error) {
        console.error('[Login] Failed to fetch test patients:', error);
      }
    };
    fetchTestPatients();
  }, []);

  console.log('[Login] Current selectedOrganization:', selectedOrganization);

  const handleLogin = async () => {
    console.log('[Login] handleLogin clicked, selectedOrganization:', selectedOrganization);
    if (!selectedOrganization) {
      alert(t('login.selectProviderAlert'));
      return;
    }

    // [RED TEAM] - Handle test mode login
    if (isTestMode) {
      if (!selectedTestPatient) {
        alert(t('login.selectTestPatientAlert'));
        return;
      }

      setIsTestModeLoading(true);
      setTestModeError(null);

      try {
        const response = await fetch('/api/auth/test-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ patientId: selectedTestPatient }),
        });

        const data = await response.json();

        if (data.success) {
          console.log('[Login] Test login successful, redirecting to dashboard');
          window.location.href = data.redirect;
        } else {
          setTestModeError(data.error || t('login.testLoginFailed'));
        }
      } catch (error) {
        console.error('[Login] Test login error:', error);
        setTestModeError(t('login.testLoginConnectionError'));
      } finally {
        setIsTestModeLoading(false);
      }
      return;
    }

    // Normal OAuth login
    const baseUrl = window.location.origin;
    const redirectUrl = `${baseUrl}/api/auth/launch?organization=${selectedOrganization.id}`;
    console.log('[Login] Redirecting to:', redirectUrl);
    window.location.replace(redirectUrl);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 shadow-lg bg-white relative">
        <div className="absolute top-4 right-4">
          <LanguageToggle />
        </div>
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img
              src="/litmed_logo.png"
              alt={t('login.logoAlt')}
              className="h-24 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-blue-600 mb-2">
            {t('login.productName')}
          </h1>
          <p className="text-lg text-blue-500 mb-2">
            {t('login.tagline')}
          </p>
          <p className="text-gray-600">
            {t('login.subtitle')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Access code gate - show when required but not verified */}
          {accessCodeLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : accessCodeStatus?.required && !accessCodeStatus?.verified ? (
            <AccessCodeGate
              onVerified={(code) => {
                setAccessCodeStatus({ required: true, verified: true, code });
              }}
            />
          ) : (
          <>
          {/* [RED TEAM] - Test mode warning banner */}
          {isTestMode && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <FlaskConical className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">{t('login.testModeWarningTitle')}</p>
                  <p>{t('login.testModeWarningBody')}</p>
                </div>
              </div>
            </div>
          )}

          {!isTestMode && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <p>{t('login.disclaimer')}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <p className="text-lg font-medium text-gray-700 mb-3">
                {isTestMode ? t('login.selectTestPatientLabel') : t('login.findOrganizationLabel')}
              </p>

              {/* [RED TEAM] - Test patient selector when in test mode */}
              {isTestMode ? (
                <div className="space-y-4">
                  <select
                    value={selectedTestPatient}
                    onChange={(e) => setSelectedTestPatient(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  >
                    <option value="">{t('login.selectTestPatientPlaceholder')}</option>
                    {testPatients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.label}
                      </option>
                    ))}
                  </select>

                  {testModeError && (
                    <div className="flex items-center space-x-2 text-red-600 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{testModeError}</span>
                    </div>
                  )}

                  <Button
                    onClick={() => setSelectedOrganization(null)}
                    variant="outline"
                    className="w-full text-gray-600"
                  >
                    {t('login.backToOrganizationSearch')}
                  </Button>
                </div>
              ) : (
                <OrganizationSearch
                  selectedOrg={selectedOrganization}
                  onSelect={(org) => {
                    console.log('[Login] Organization selected:', org);
                    setSelectedOrganization(org);
                  }}
                  placeholder={t('login.searchOrganizationPlaceholder')}
                  className="w-full"
                />
              )}
            </div>

            <Button
              onClick={handleLogin}
              disabled={isTestMode ? !selectedTestPatient || isTestModeLoading : !selectedOrganization}
              className={`w-full py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
                isTestMode
                  ? 'bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white'
              }`}
            >
              {isTestMode ? (
                <>
                  <FlaskConical className="h-5 w-5" />
                  <span>{isTestModeLoading ? t('login.testModeLoading') : t('login.enterTestMode')}</span>
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5" />
                  <span>{t('login.signIn')}</span>
                </>
              )}
            </Button>

            {import.meta.env.DEV && (
              <div className="text-xs text-gray-500 space-y-1 mt-2 p-2 bg-gray-50 rounded">
                <p className="font-semibold">{t('login.testEnvironmentInfoTitle')}</p>
                <p>{t('login.testEnvironmentSandboxNote')}</p>
                <p>{t('login.testEnvironmentProductionNote')}</p>
                <p>{t('login.testEnvironmentCurrentMode', { mode: import.meta.env.VITE_APP_MODE || 'sandbox' })}</p>
              </div>
            )}
          </div>

          <p className="text-xs text-center text-gray-500">
            {t('login.termsAgreement')}{' '}
            <a href="https://previsit.mylitmed.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('login.termsOfService')}</a>
            {' '}{t('login.and')}{' '}
            <a href="https://previsit.mylitmed.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('login.privacyPolicy')}</a>.
          </p>
          </>
          )}
        </div>

        {/* Rendered in the OPPOSITE language on purpose: a reader stuck on the
            wrong-language screen needs the hint in the language they read,
            not the one currently shown. Do not "fix" en/es bundle values for this key. */}
        <p className="text-xs text-text-secondary text-center mt-6">
          {t('login.languageFootnote')}
        </p>
      </Card>
    </div>
  );
};

export default Login;
